import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Users, Repeat, Award, ArrowLeft, ShoppingCart, Wallet } from 'lucide-react';
import { usePaystackPayment } from 'react-paystack';
import { useRaffle } from '../hooks/useRaffles';
import { usePurchaseTicket } from '../hooks/useTickets';
import { useWallet } from '../hooks/useWallet';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Spinner } from '../components/common/Spinner';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Modal } from '../components/common/Modal';
import { CountdownTimer } from '../components/common/CountdownTimer';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import { RAFFLE_STATUS_COLORS, PAYMENT_PROVIDERS } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../config/api';

export const RaffleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useRaffle(id!);
  const { isAuthenticated, user } = useAuth();
  const { data: walletData } = useWallet({ enabled: isAuthenticated });
  const purchaseTicket = usePurchaseTicket();
  const initializePaystack = usePaystackPayment({ publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY });
  
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [paymentProvider, setPaymentProvider] = useState('PAYSTACK');
  const [accessCode, setAccessCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const verifyPayment = useCallback(async (reference: string) => {
    try {
      await api.post('/webhooks/paystack/verify', { reference });
      toast.success('Payment successful! Tickets purchased.');
      window.location.reload();
    } catch {
      toast.success('Payment received. It may take a moment to process.');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  const raffle = data?.data;
  if (!raffle) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Card><p className="text-center text-gray-500 dark:text-gray-400 py-8">Raffle not found.</p></Card></Container>
      </div>
    );
  }

  const totalCost = raffle.ticketPrice * quantity;
  const prizeArray = raffle.prizeDistribution.split(',').map(p => parseFloat(p.trim()));
  const winnersShare = prizeArray.reduce((a, b) => a + b, 0);
  const pot = raffle.nextDraw?.sales ?? raffle.currentDrawSales ?? 0;
  const ticketsSold = raffle.nextDraw?.ticketsSold ?? raffle.currentDrawTicketsSold ?? 0;
  const nextDrawTime = raffle.nextDraw?.scheduledTime;

  const handlePurchase = async () => {
    const walletBalance = isAuthenticated ? parseFloat(String(walletData?.data?.balance || 0)) : 0;
    const effectiveProvider = isAuthenticated && walletBalance >= totalCost ? 'WALLET' : paymentProvider;
    const response = await purchaseTicket.mutateAsync({
      raffleId: raffle.id, quantity, paymentProvider: effectiveProvider as any,
      accessCode: accessCode || undefined, firstName: firstName || undefined,
      lastName: lastName || undefined, email: email || undefined, phone: phone || undefined,
    });
    const data = response.data;
    if (data?.requiresPayment && data?.paymentData?.provider === 'paystack' && data?.paymentData?.reference) {
      setShowPurchaseModal(false);
      initializePaystack({
        config: { email: user?.email || email || '', amount: (data.amountNeeded || 0) * 100, reference: data.paymentData.reference, currency: raffle.currency },
        onSuccess: (ref: any) => verifyPayment(ref?.reference || data.paymentData?.reference),
        onClose: () => toast('Payment cancelled', { icon: 'ℹ️' }),
      });
      return;
    }
    if (data?.requiresPayment && data?.paymentData) {
      const paymentUrl = data.paymentData.authorizationUrl || data.paymentData.url;
      if (paymentUrl) { window.location.href = paymentUrl; return; }
    }
    setShowPurchaseModal(false); setQuantity(1); setAccessCode(''); setFirstName(''); setLastName(''); setEmail(''); setPhone('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      {/* Hero — image with overlay */}
      <div className="relative">
        <div className="h-64 md:h-80 overflow-hidden">
          {raffle.imageUrl ? (
            <img src={raffle.imageUrl} alt={raffle.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        </div>
        <Container className="absolute bottom-0 left-0 right-0 pb-6">
          <Link to="/raffles" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Raffles
          </Link>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <Badge variant={RAFFLE_STATUS_COLORS[raffle.status]}>{raffle.status}</Badge>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white mt-2">{raffle.name}</h1>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">Current Pot</p>
              <p className="text-3xl font-extrabold text-white">{formatCurrency(pot, raffle.currency)}</p>
            </div>
          </div>
        </Container>
      </div>

      {/* Countdown bar */}
      {nextDrawTime && (
        <div className="bg-indigo-900 py-4">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-300" />
                <span className="text-sm text-indigo-100 font-medium">Next draw in</span>
              </div>
              <CountdownTimer target={nextDrawTime} variant="card" onDark />
              <span className="text-sm text-indigo-300">
                ({new Date(nextDrawTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})
              </span>
            </div>
          </Container>
        </div>
      )}

      <Container className="py-8">
        {/* Mobile buy button */}
        <div className="lg:hidden mb-6">
          {raffle.status === 'ACTIVE' && (
            <Button fullWidth size="lg" onClick={() => setShowPurchaseModal(true)}>
              {isAuthenticated ? 'Buy Tickets' : 'Buy as Guest'} — {formatCurrency(raffle.ticketPrice, raffle.currency)}/ticket
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{raffle.description}</p>
            </Card>

            {/* Stats — single card */}
            <Card>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: ShoppingCart, label: 'Ticket Price', value: formatCurrency(raffle.ticketPrice, raffle.currency), color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950' },
                  { icon: Users, label: 'Winners', value: String(raffle.numberOfWinners), color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950' },
                  { icon: Repeat, label: 'Schedule', value: raffle.drawSchedule.replace('_', ' '), color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950' },
                ].map(({ icon: Icon, label, value, color, bg }) => (
                  <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
                    <Icon className={`h-4 w-4 ${color} mx-auto mb-1.5`} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Prize Distribution */}
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Award className="h-5 w-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Prize Distribution</h3>
              </div>
              <div className="space-y-2">
                {prizeArray.map((pct, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-xs font-bold text-indigo-700 dark:text-indigo-400 flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(pct / winnersShare) * 100}%` }} />
                      </div>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100 w-12 text-right">{formatPercentage(pct)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Winners Share</span>
                <span className="font-bold text-emerald-600">{formatPercentage(winnersShare)}</span>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div>
            <Card className="sticky top-24">
              {/* Draw info */}
              <div className="mb-5 pb-5 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Draw Info</h3>
                </div>

                {raffle.pendingDraw && (
                  <div className="bg-amber-50 dark:bg-amber-950 rounded-xl p-3 mb-3 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Pending Draw</p>
                      <Badge variant="warning">DELAYED</Badge>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                      {new Date(raffle.pendingDraw.scheduledTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(raffle.pendingDraw.scheduledTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Processing — results coming soon</p>
                  </div>
                )}

                {raffle.nextDraw && (
                  <div className={`${raffle.pendingDraw ? 'bg-emerald-50 dark:bg-emerald-950 border-emerald-100 dark:border-emerald-900' : 'bg-indigo-50 dark:bg-indigo-950 border-indigo-100 dark:border-indigo-900'} rounded-xl p-3 border`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-sm font-medium ${raffle.pendingDraw ? 'text-emerald-700 dark:text-emerald-400' : 'text-indigo-600 dark:text-indigo-400'}`}>Next Draw</p>
                      <Badge variant={raffle.pendingDraw ? 'success' : 'info'}>{raffle.nextDraw.status}</Badge>
                    </div>
                    <div className="mb-3">
                      <CountdownTimer target={raffle.nextDraw.scheduledTime} variant="card" large />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(raffle.nextDraw.scheduledTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(raffle.nextDraw.scheduledTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                )}

                {/* Current Pot */}
                <div className="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-3 mt-3 border border-emerald-100 dark:border-emerald-900">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Current Draw Pot</p>
                  <p className="text-xl font-extrabold text-emerald-700 dark:text-emerald-400 mt-0.5">{formatCurrency(pot, raffle.currency)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ticketsSold} tickets sold</p>
                </div>

                <div className="mt-3 text-sm">
                  <div className="flex justify-between py-1.5">
                    <span className="text-gray-500 dark:text-gray-400">Schedule</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{raffle.drawSchedule.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {raffle.status === 'ACTIVE' && (
                  <>
                    {isAuthenticated ? (
                      <Button fullWidth size="lg" onClick={() => setShowPurchaseModal(true)}>Buy Tickets</Button>
                    ) : (
                      <Button fullWidth size="lg" onClick={() => setShowPurchaseModal(true)} variant="secondary">Buy as Guest</Button>
                    )}
                    {(user?.role === 'AGENT' || user?.role === 'ADMIN') && (
                      <Link to={`/raffles/${raffle.id}/countdown`}>
                        <Button fullWidth size="lg" variant="outline">View Countdown</Button>
                      </Link>
                    )}
                  </>
                )}
                <Link to={`/draw-history/${raffle.id}`}>
                  <Button fullWidth size="lg" variant="outline">View Draw History</Button>
                </Link>
              </div>
              
              {!isAuthenticated && (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center mt-5">
                  <Link to="/login" className="text-indigo-600 hover:text-indigo-700">Login</Link> or{' '}
                  <Link to="/register" className="text-indigo-600 hover:text-indigo-700">Sign up</Link> for the full experience
                </p>
              )}
            </Card>
          </div>
        </div>
      </Container>

      {/* Purchase Modal */}
      <Modal isOpen={showPurchaseModal} onClose={() => setShowPurchaseModal(false)} title={isAuthenticated ? 'Purchase Tickets' : 'Purchase Tickets as Guest'}>
        <div className="space-y-4">
          {!isAuthenticated && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input label="First Name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
                <Input label="Last Name *" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
              </div>
              <Input label="Email *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" />
              <Input label="Phone Number (Optional)" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1234567890" helperText="Include country code. Required for SMS notifications." />
              <p className="text-sm text-gray-500 dark:text-gray-400">An account will be created for you automatically.</p>
            </>
          )}
          <Input label="Quantity" type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} />
          {raffle.participationAccess === 'INVITE_CODE' && (
            <Input label="Access Code" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} placeholder="Enter access code" />
          )}
          {(() => {
            const walletBalance = isAuthenticated ? parseFloat(String(walletData?.data?.balance || 0)) : 0;
            const hasSufficientBalance = isAuthenticated && walletBalance >= totalCost;
            const shortfall = totalCost - walletBalance;
            return (
              <>
                {isAuthenticated && (
                  <div className="bg-indigo-50 dark:bg-indigo-950 p-3 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Wallet Balance</span>
                    </div>
                    <span className={`text-sm font-bold ${hasSufficientBalance ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {formatCurrency(walletBalance, raffle.currency)}
                    </span>
                  </div>
                )}
                {isAuthenticated && hasSufficientBalance && (
                  <div className="bg-emerald-50 dark:bg-emerald-950 p-3 rounded-xl text-sm text-emerald-700 dark:text-emerald-400">
                    ✓ Your wallet balance covers this purchase.
                  </div>
                )}
                {(!isAuthenticated || !hasSufficientBalance) && (
                  <>
                    {isAuthenticated && (
                      <div className="bg-amber-50 dark:bg-amber-950 p-3 rounded-xl text-sm text-amber-700 dark:text-amber-400">
                        Insufficient balance. You need {formatCurrency(shortfall, raffle.currency)} more.
                      </div>
                    )}
                    <Select label="Payment Method" value={paymentProvider} onChange={(e) => setPaymentProvider(e.target.value)}
                      options={PAYMENT_PROVIDERS.filter(p => p.value !== 'WALLET')} />
                  </>
                )}
              </>
            );
          })()}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Ticket Price</span>
              <span>{formatCurrency(raffle.ticketPrice, raffle.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Quantity</span>
              <span>{quantity}</span>
            </div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200 dark:border-gray-700">
              <span>Total</span>
              <span className="text-indigo-600">{formatCurrency(totalCost, raffle.currency)}</span>
            </div>
          </div>
          <Button fullWidth onClick={handlePurchase} isLoading={purchaseTicket.isPending} disabled={!isAuthenticated && (!firstName || !lastName || !email)}>
            Confirm Purchase
          </Button>
        </div>
      </Modal>
    </div>
  );
};
